<template>
  <div class="px-2 lg:pr-2 lg:ml-4 space-y-4 lg:space-y-5 mb-4">

    <!-- User Profile Card -->
    <div class="bg-white rounded-xl border border-gray-200 shadow-md p-4 md:p-6">
      <div class="tw-flex flex-col items-center text-center space-y-4">
        <div class="relative">
          <avatar
            :src="user.photo_url" :alt="user.name" allow-edit
            :edit-text="$t('profile_upload_avatar')"
            width="100px"
            height="100px"
            class="h-20 w-20 md:h-24 md:w-24 border border-primary rounded-full cursor-pointer"
          />
          <image-input
            v-model="user.photo_url" @update:modelValue="updateProfilePicture"
            width="100" height="100"
            class="w-20 md:w-24 border-0 absolute transform -translate-x-1/2 h-20 md:h-20 opacity-0 z-10 left-[45%] top-[0]"
            style="width: 100px; position: absolute; top: 0; left: 45%; height: 100px;"
          />
        </div>

        <div class="space-y-2">
          <div class="text-primary font-medium text-lg md:text-xl">
            {{ user.name }}
          </div>
          <div class="tw-flex justify-center">
            <UserSocialButtons :user="userBaseInfoData" v-if="userBaseInfoData"/>
          </div>
        </div>
      </div>
      <hr v-if="user.bio" class="my-2 border-gray-200"/>
      <div v-if="user.bio" class="text-sm leading-4 my-2">
        {{ user.bio }}
      </div>
      <div class="tw-flex flex-col">
        <ds-button
          class="!mr-0"
          size="small" variant="rounded-secondary" :label="$t('profile_settings_update')"
          @click="showUpdateProfileModal"
        />
        <a href="#password-and-security" class="text-primary hover:underline text-center mt-2 cursor-pointer text-sm">{{ $t('profile_password_security') }}</a>
      </div>
    </div>

    <!-- Actors Card -->
    <div class="bg-white rounded-xl border border-gray-200 shadow-md p-4 tw-flex flex-col" v-if="showClaims" id="introjs-after-onboarding-actors">
      <div class="text-left text-primary font-medium mb-3">
        {{ $t('profile_settings_my_actors') }}
      </div>
      <p v-if="!actorClaims || !actorClaims.length" class="text-gray-600 text-sm mb-0">
        {{ $t('profile_no_actors_found') }}
      </p>
      <div class="space-y-3">
        <div
          v-for="(claim, index) in slicedActorClaims" :key="'actor' + index"
          class="border-b border-gray-100 pb-2 last:border-b-0"
        >
          <div class="tw-flex items-start space-x-3">
            <ActorAvatar :actor="claim" :width="50"/>
            <div class="tw-flex-1 min-w-0">
              <div class="font-medium text-gray-900 truncate">
                {{ claim.name }}
              </div>
              <router-link
                :to="'/actors/' + claim.id" class="text-primary text-sm underline hover:no-underline"
              >
                {{ $t('profile_settings_actor_view') }}
              </router-link>
            </div>
          </div>
        </div>
        <a
          v-if="actorClaims.length > 5"
          @click="showMoreactorClaims = !showMoreactorClaims"
          class="text-primary text-sm cursor-pointer hover:underline"
        >{{
            showMoreactorClaims ? $t('actor_detail_show_less') : $t('actor_detail_show_all')
          }}</a>
      </div>
      <ds-button
        v-if="showAddActorButton"
        class="mt-3"
        size="small" variant="rounded-secondary"
        :label="$t('profile_settings_add_actor')"
        @click="showCreateActorModal"
      />
    </div>

    <!-- Products Card -->
    <div
      class="bg-white rounded-xl border border-gray-200 shadow-md p-4 tw-flex flex-col"
      v-if="showProductClaims"
    >
      <div class="text-left text-primary font-medium mb-3">
        {{ $t('profile_settings_my_products', { products: productsLabel }) }}
      </div>
      <p v-if="!slicedProductClaims || !slicedProductClaims.length" class="text-gray-600 text-sm mb-0">
        {{ $t('profile_no_products_found') }}
      </p>
      <div class="space-y-3">
        <div
          v-for="(claim, index) in slicedProductClaims" :key="'product' + index"
          class="border-b border-gray-100 pb-2 last:border-b-0"
        >
          <div class="tw-flex items-start">
            <div class="tw-flex-1 min-w-0">
              <div class="font-medium text-gray-900 truncate">
                {{ claim.name }}
              </div>
              <router-link
                :to="'/actors/' + claim.id" class="text-primary text-sm underline hover:no-underline"
              >
                {{ $t('profile_settings_product_view') }}
              </router-link>
            </div>
          </div>
        </div>
        <a
          v-if="productClaims.length > 5"
          @click="showMoreProductClaims = !showMoreProductClaims"
          class="text-primary text-sm cursor-pointer hover:underline"
        >{{
            showMoreProductClaims ? $t('actor_detail_show_less') : $t('actor_detail_show_all')
          }}</a>
      </div>
      <ds-button
        v-if="showAddProductButton && slicedProductClaims && slicedProductClaims.length"
        class="mt-3 text-center"
        size="small" variant="rounded-secondary"
        :label="$t('add_product', { product: productLabel })"
        @click="actorClaims.length === 0 ? showRestrictedModal() : showAddProductModal()"
      />
    </div>

    <!-- Knowledge Base Card -->
    <div class="bg-white rounded-xl border border-gray-200 shadow-md p-4 tw-flex flex-col" v-if="displayKnowledgeBase">
      <div class="text-left text-primary font-medium mb-3">
        {{ $t('profile_settings_my_knowledge') }}
      </div>
      <div class="space-y-3" v-if="myKnowledgeBasePosts && myKnowledgeBasePosts.length > 0">
        <div
          v-for="(post, index) in slicedMyKnowledgeBasePosts" :key="'post' + index"
          class="border-b border-gray-100 pb-2 last:border-b-0"
        >
          <div class="tw-flex flex-col w-full">
            <div class="tw-flex justify-between items-start mb-1">
              <div class="font-medium text-gray-900 tw-flex-1 truncate">
                {{ post.title }}
              </div>
            </div>
            <router-link
              class="text-primary text-sm underline hover:no-underline"
              :to="post.community_id ? `/communities/${post.community_id}/knowledge-base/posts/${post.id}` : `/knowledge-base/posts/${post.id}`"
            >
              {{ $t('profile_knowledge_base_post_view') }}
            </router-link>
          </div>
        </div>
        <a
          v-if="myKnowledgeBasePosts.length > 3"
          @click="showMoreKnowledgeBasePosts = !showMoreKnowledgeBasePosts"
          class="text-primary text-sm cursor-pointer hover:underline"
        >{{
            showMoreKnowledgeBasePosts ? $t('actor_detail_show_less') : $t('actor_detail_show_all')
          }}</a>
      </div>
      <p v-else class="text-gray-600 text-sm">
        {{ $t('profile_no_knowledge_base_posts_found') }}

        <DsButton :label="$t('profile_browse_knowledge_base')" variant="rounded-secondary" size="small" class="w-full mt-2" to="/dashboards/knowledge-base"/>
      </p>
    </div>

    <!-- Communities Card -->
    <div
      class="community-card bg-white rounded-xl border border-gray-200 shadow-md p-4 md:p-6 tw-flex flex-col transition-all duration-500"
      :class="{
        'ring-4 ring-primary-community ring-opacity-30 bg-primary-community-extra-lightest border-primary-community shadow-lg transform scale-[1.02]': highlightCommunityCard
      }"
      v-if="$store.getters.canUsePublicAnnouncements && $store.getters.hasAccessToCommunities"
    >
      <div class="text-left text-primary font-medium mb-4 text-base md:text-lg">
        {{ $t('profile_settings_my_communities') }}
      </div>
      <div class="space-y-4" v-if="slicedCommunities && slicedCommunities.length">
        <div
          v-for="(community, index) in slicedCommunities" :key="'community' + index"
          class="border-b border-gray-100 pb-4 last:border-b-0 last:pb-0"
        >
          <div class="space-y-2">
            <div class="tw-flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <span class="text-gray-900 !text-xs font-medium md:text-base truncate">{{ community.name }}</span>
              <label class="tw-flex items-center text-xs flex-shrink-0" v-if="communities && communities.length >= 1">
                <input
                  type="checkbox"
                  v-tooltip.top="getPreferredCommunityId() === community.id ? 'This is your preferred community' : 'This checkbox marks your preferred community'"
                  :checked="getPreferredCommunityId() === community.id"
                  @change="toggleCommunityPreference(community.id)"
                  class="mr-2 w-4 h-4 text-primary-community bg-gray-100 border-gray-300 rounded focus:ring-primary-community focus:ring-2"
                  :class="{ 'animate-pulse': highlightCommunityCard }"
                />
                <span class="text-xs text-gray-600" :class="{ 'font-medium text-primary-community': highlightCommunityCard }">
                    {{ getPreferredCommunityId() === community.id ? $t('profile_preferred') : $t('profile_set_as_preferred') }}
                  </span>
              </label>
            </div>
            <span class="text-orange-500 text-xs tw-flex items-center gap-1" v-if="!hasCommunityAccessBasedOnAccessLevel(community)">
                <icon name="warning" size="14"></icon>
                {{ $t('communities_warning_claim_actor_short') }}
              </span>
            <div class="tw-flex flex-row justify-between items-center mt-2 space-y-2">
              <a class="text-primary text-sm underline hover:no-underline cursor-pointer" @click="viewCommunity(community)">
                {{ $t('profile_settings_community_view') }}
              </a>
              <DsButton
                :label="isCommunityManager(community) ? $t('communities_manage') : $t('communities_leave')"
                variant="rounded-secondary"
                size="extra-small"
                @click="isCommunityManager(community) ? $router.push('/communities/' + community.id + '/members') : openConfirmationModal(community)"
              />
            </div>
          </div>
        </div>
      </div>
      <a
        v-if="communities.length > 5"
        @click="showMoreCommunities = !showMoreCommunities"
        class="text-primary text-sm cursor-pointer hover:underline"
      >{{
          showMoreCommunities ? $t('actor_detail_show_less') : $t('actor_detail_show_all')
        }}</a>
    </div>
    <div v-else>
      {{ $t('profile_no_communities_joined') }}

      <DsButton :label="$t('profile_browse_communities')" variant="rounded-secondary" size="small" class="w-full mt-2" to="/communities"/>
    </div>

    <!-- Spotting Areas Card -->
    <div
      class="bg-white rounded-xl border border-gray-200 shadow-md p-4 md:p-6 tw-flex flex-col transition-all duration-500"
      :class="{
          'ring-4 ring-primary-community ring-opacity-30 bg-primary-community-extra-lightest border-primary-community shadow-lg transform scale-[1.02]': highlightSpottingAreaCard
        }"
      v-if="$store.getters.hasAccessToNewSpottingAreas"
    >
      <div class="text-left text-primary font-medium mb-4 text-base md:text-lg">
        {{ $t('profile_settings_my_spotting_areas', { spottingareas: spottingAreasLabel }) }}
      </div>
      <div class="space-y-4" v-if="slicedSpottingAreas && slicedSpottingAreas.length > 0">
        <div
          v-for="(spottingArea, index) in slicedSpottingAreas" :key="'spotting-area' + index"
          class="border-b border-gray-100 pb-4 last:border-b-0 last:pb-0"
        >
          <div class="space-y-2">
            <div class="tw-flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div class="tw-flex items-center gap-2 min-w-0 flex-1">
                <span class="text-gray-900 font-medium !text-xs md:text-base truncate">{{ spottingArea.name }}</span>
              </div>
              <label class="tw-flex items-center text-xs flex-shrink-0" v-if="spottingAreas && spottingAreas.length > 1">
                <input
                  type="checkbox"
                  v-tooltip.top="getPreferredSpottingAreaId() === spottingArea.id ? 'This is your preferred spotting area' : 'This checkbox marks your preferred spotting area'"
                  :checked="getPreferredSpottingAreaId() === spottingArea.id"
                  @change="toggleSpottingAreaPreference(spottingArea.id)"
                  class="mr-2 w-4 h-4 text-primary-community bg-gray-100 border-gray-300 rounded focus:ring-primary-community focus:ring-2"
                  :class="{ 'animate-pulse': highlightSpottingAreaCard }"
                />
                <span class="text-xs text-gray-600" :class="{ 'font-medium text-primary-community': highlightSpottingAreaCard }">
                    {{ getPreferredSpottingAreaId() === spottingArea.id ? $t('profile_preferred') : $t('profile_set_as_preferred') }}
                  </span>
              </label>
            </div>
            <span class="text-orange-500 text-xs tw-flex items-center gap-1" v-if="!hasCommunityAccessBasedOnAccessLevel(spottingArea)">
                <icon name="warning" size="14"></icon>
                {{ $t('communities_warning_claim_actor_short') }}
              </span>
            <div class="tw-flex flex-row mt-3 space-y-2">
              <a class="text-primary text-sm underline hover:no-underline cursor-pointer" @click="viewSpottingArea(spottingArea)">
                {{ $t('profile_settings_spotting_area_view', { spottingarea: spottingAreaLabel }) }}
              </a>
            </div>
          </div>
        </div>
      </div>
      <a
        v-if="spottingAreas.length > 5"
        @click="showMoreSpottingAreas = !showMoreSpottingAreas"
        class="text-primary text-sm cursor-pointer hover:underline"
      >{{
          showMoreSpottingAreas ? $t('actor_detail_show_less') : $t('actor_detail_show_all')
        }}</a>
    </div>
    <div v-else>
      {{ $t('profile_no_spotting_areas_access') }}

      <DsButton :label="$t('profile_browse_spotting_areas')" variant="rounded-secondary" size="small" class="w-full mt-2" to="/spotting-areas"/>
    </div>
  </div>
</template>

<script>
  import Avatar from '../Avatar/Avatar.vue'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '../../store/modules/ui.js'
  import MODAL_IDS from '../../constants/modal-ids.js'
  import ImageInput from '../Form/ImageInput.vue'
  import { fetchProfile, getUserPreferences, updateProfile, updateUserPreferences } from '../../api/user.js'
  import { fetchPostsByUser } from '../../api/knowledge-base.js'
  import { prepareUserForEditing } from '../../util/user-settings.js'
  import { trackHeapEvent } from '../../util/analytics.js'
  import { actorLogo } from '../../util/actor'
  import TranslationsMixin from '../../util/TranslationsMixin.js'
  import OnboardingMixin from '../../util/OnboardingMixin.js'
  import { ACTION_TYPES as COMMUNITY_ACTION_TYPES } from '../../store/modules/communities.js'
  import { MUTATION_TYPES as FILTERS_MUTATION_TYPES } from '../../store/modules/filters'
  import { updateActor } from '../../api/actors.js'
  import UserSocialButtons from '@/Domain/User/Components/UserSocialButtons.vue'
  import { transformLoggedInUserToUserBaseInfoData } from '@/Domain/User/Api/UserApiTransformers'
  import CommunityMixin from '@/util/CommunityMixin'
  import { leaveCommunity } from '@/api/communities.js'
  import DsButton from '@/components/DsButton/DsButton.vue'
  import ActorAvatar from '@/Domain/Actor/Components/ActorAvatar.vue'
  import SpottingAreaMixin from '@/util/SpottingAreaMixin'
  import UserMixin from '@/util/UserMixin'

  export default {
    name: 'ProfileCardsSection',
    props: {
      isPersonalHome: {
        type: Boolean,
        default: false
      },
      showCards: {
        type: Boolean,
        default: true
      }
    },
    data () {
      return {
        user: {},
        showMoreactorClaims: false,
        showMoreProductClaims: false,
        showMoreCommunities: false,
        showMoreSpottingAreas: false,
        showMoreKnowledgeBasePosts: false,
        myKnowledgeBasePosts: [],
        userPreferences: {
          preferredCommunityId: null,
          preferredSpottingAreaId: null,
        },
        highlightCommunityCard: false,
        highlightSpottingAreaCard: false,
      }
    },
    computed: {
      userBaseInfoData () {
        if (!this.originalProfile?.id) {
          return null
        }
        return transformLoggedInUserToUserBaseInfoData(this.originalProfile)
      },
      modalContext () {
        return this.$store.state.ui.modalContext
      },
      originalProfile () {
        return this.$store.state.user.profile
      },
      slicedActorClaims () {
        if (this.showMoreactorClaims) {
          return this.actorClaims
        }
        return this.actorClaims.slice(0, 5)
      },
      actorClaims () {
        if (!this.originalProfile || !this.originalProfile.claims) {
          return []
        }
        return this.checkForDuplicates(this.originalProfile.claims.filter(claim => claim.actor_type !== 'Product'))
      },
      showClaims () {
        console.log(this.$store.getters.userCanCreate, this.$store.getters.claimableActorTypes)
        if (this.actorClaims.length > 0) {
          return true
        }

        if (this.isMember) {
          return true
        }


        return this.$store.getters.userCanCreate &&
          (this.$store.getters.claimableActorTypes.includes('LegalEntity') || this.$store.getters.claimableActorTypes.includes('Person'))
      },
      showProductClaims () {
        if (this.actorClaims.length > 0) {
          return true
        }
        return this.$store.getters.userCanCreate && this.$store.getters.claimableActorTypes.includes('Product')
      },
      productClaimsFromActors () {
        let result = []
        if (this.actorClaims.length > 0) {
          for (const nonProductClaim of this.actorClaims) {
            if (nonProductClaim && nonProductClaim.products && nonProductClaim.products.length) {
              result = [...result, ...nonProductClaim.products]
            }
          }
        }
        return result
      },
      slicedProductClaims () {
        if (this.showMoreProductClaims) {
          return this.productClaims
        }
        return this.productClaims.slice(0, 5)
      },
      productClaims () {
        if (!this.originalProfile || !this.originalProfile.claims) {
          return []
        }
        return this.checkForDuplicates([...this.productClaimsFromActors, ...this.originalProfile.claims.filter(claim => claim.actor_type === 'Product')])
      },
      showAddActorButton () {
        if (this.isMember) {
          return true
        }
        return this.$store.getters.userCanCreate && this.$store.getters.claimableActorTypes && this.$store.getters.claimableActorTypes.length > 0
      },
      showAddProductButton () {
        if (this.isMember) {
          return true
        }
        return this.$store.getters.userCanCreate && this.$store.getters.claimableActorTypes && this.$store.getters.claimableActorTypes.includes('Product')
      },
      isMember () {
        return this.$store.getters.isMember
      },
      slicedSpottingAreas () {
        if (this.showMoreSpottingAreas) {
          return this.spottingAreas
        }
        return this.spottingAreas.slice(0, 5)
      },
      spottingAreas () {
        if (this.$store.getters.isMember) {
          return this.$store.state.spottingAreas.listData.data
        }

        return this.joinedSpottingAreas
      },
      slicedCommunities () {
        if (this.showMoreCommunities) {
          return this.communities
        }
        return this.communities.slice(0, 5)
      },
      communities () {
        if (this.$store.getters.isMember) { // Owner / members will see all the communities
          return this.$store.state.communities.listData.data
        }

        return this.joinedCommunities
      },
      onboardingTextConfig () {
        return this.$store.state.config.onboardingTextConfig
      },
      displayKnowledgeBase () {
        return this.$store.getters.hasAccessToKnowledgeBase &&
          this.$store.state.config.viewDashboard &&
          this.$store.state.config.viewDashboard.includes('knowledge-base') &&
          ((this.myKnowledgeBasePosts && this.myKnowledgeBasePosts.length) || this.canCreateResource)
      },
      canCreateResource () {
        return this.$store.getters.canUserUploadContent
      },
      slicedMyKnowledgeBasePosts () {
        if (this.showMoreKnowledgeBasePosts) {
          return this.myKnowledgeBasePosts
        }
        return this.myKnowledgeBasePosts.slice(0, 3)
      },
    },
    methods: {
      getFakePreferredCommunityId () {
        if (this.profile && this.profile.joinedCommunityIds && this.profile.joinedCommunityIds.length > 0) {
          return this.profile.joinedCommunityIds[0]
        }

        return null
      },
      getPreferredCommunityId () {
        // Use preferred community if set, otherwise fall back to first available
        if (this.userPreferences.preferredCommunityId) {
          return this.userPreferences.preferredCommunityId
        }

        return null
      },
      getFakePreferredSpottingAreaId () {
        if (this.profile && this.profile.accessibleSpottingAreas && this.profile.accessibleSpottingAreas.length > 0) {
          return this.profile.accessibleSpottingAreas[0]
        }

        return null
      },
      getPreferredSpottingAreaId () {
        // Use preferred spotting area if set, otherwise fall back to first available
        if (this.userPreferences.preferredSpottingAreaId) {
          return this.userPreferences.preferredSpottingAreaId
        }

        return null
      },
      currentUser () {
        const profile = this.$store.state.user.profile
        return prepareUserForEditing(profile)
      },
      updateProfilePicture () {
        updateProfile({ photo_url: this.user.photo_url })
          .then(data => {
            this.$store.commit('USER/UPDATE_PROFILE', data)
            this.user = this.currentUser()
            this.$emit('updatedProfilePicture')
            trackHeapEvent('updateProfilePicture.success')
          })
          .catch(errors => {
            trackHeapEvent('updateProfilePicture.failure', { errorMessage: JSON.stringify(errors) })
          })
      },
      imageStyle (claim) {
        if (this.companyLogoUrl(claim)) {
          return { backgroundImage: `url(${this.companyLogoUrl(claim)})` }
        }
      },
      companyLogoUrl (claim) {
        return actorLogo(claim)
      },
      isCommunityManager (community) {
        return community.isManager
      },
      openConfirmationModal (community) {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
          title: this.$t('communities_leave_confirmation_title', { community: community.name }),
          body: this.$t('communities_leave_confirmation_message'),
          confirmLabel: this.$t('personal_home_relevant_yes'),
          modalBodyClasses: 'empty-fields-list',
          type: 'check-empty-fields',
          cancelLabel: this.$t('personal_home_relevant_no'),
          isCancellable: true,
          communityId: community.id,
        })
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.CONFIRMATION)
      },
      leaveCommunity () {
        leaveCommunity(this.modalContext?.communityId)
          .then(() => {
            fetchProfile()
              .then(profile => {
                this.$store.commit('USER/UPDATE_PROFILE', profile)
                this.$bus.emit('updateUserProfile')
              })
          })
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, null)
      },
      showUpdateProfileModal () {
        trackHeapEvent('clickUpdateProfile')
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.EDIT_USER_PROFILE)
      },
      loadCommunities () {
        if ((!this.communities || !this.communities.length) && !this.$store.state.communities.listData.loading) {
          this.$store.dispatch(COMMUNITY_ACTION_TYPES.FETCH_COMMUNITIES_LIST)
        }
      },
      showRestrictedModal () {
        trackHeapEvent('restrictedModal')
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.RESTRICTED_MODAL)
      },
      showAddProductModal () {
        trackHeapEvent('addProduct')
        const modalContext = {
          prefilled: {},
          successCallback: actor => {
            this.$bus.emit('productAddedViaOverlay', actor)
          },
        }
        if (this.$store.getters.detailActor && this.$store.getters.detailActor.address && (this.$store.getters.detailActor.address.street || this.$store.getters.detailActor.address.city)) {
          modalContext.prefilled.address = {
            ...this.$store.getters.detailActor.address,
          }
          modalContext.prefilled.parentName = this.$store.getters.detailActor.name
        }
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, modalContext)
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.ADD_PRODUCT)
      },
      viewCommunity (community) {
        this.$store.commit(FILTERS_MUTATION_TYPES.CLEAR)
        this.$store.commit(FILTERS_MUTATION_TYPES.SET_PORTFOLIO, community.portfolioId)
        this.$router.push('/communities/' + community.id)
      },
      viewSpottingArea (spottingArea) {
        this.$store.commit(FILTERS_MUTATION_TYPES.CLEAR)
        this.$router.push('/spotting-areas/' + spottingArea.id)
      },
      fetchMyKnowledgeBasePosts () {
        const userId = this.originalProfile?.id
        if (userId && typeof userId === 'number' && userId > 0) {
          fetchPostsByUser(userId, { limit: 5 })
            .then((result) => {
              this.myKnowledgeBasePosts = result.data.data || []
            })
            .catch((error) => {
              console.error('Error fetching knowledge base posts:', error)
              this.myKnowledgeBasePosts = []
            })
        } else {
          // If no valid user ID, set empty array
          this.myKnowledgeBasePosts = []
        }
      },
      showCreateActorModal () {
        trackHeapEvent('clickAddActorOnUserProfile')
        if (this.$store.getters.isPublisherEnabled) {
          this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, { forceClaim: true })
        }
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.ADD_ACTOR)
      },
      showCreateResourcePanel () {
        this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, {
          component: 'create-resource-panel',
          metaData: { reach: 'public' },
        })
      },
      checkForDuplicates (array) {
        const filteredArray = []
        const claims = array.map(function (item) {
          return item.name
        })
        const filteredClaims = [...new Set(claims)]
        filteredClaims.map(function (value) {
          const claim = array.find(function (item) {
            return item.name === value
          })
          if (!filteredArray.includes(claim)) {
            filteredArray.push(claim)
          }
        })
        return filteredArray
      },
      async loadUserPreferences () {
        try {
          const response = await getUserPreferences()
          this.userPreferences = response.preferences
        } catch (error) {
          console.error('Error loading user preferences:', error)
        }
      },
      async toggleCommunityPreference (communityId) {
        try {
          const newPreferredCommunityId = this.userPreferences.preferredCommunityId === communityId ? null : communityId
          await updateUserPreferences({
            preferredCommunityId: newPreferredCommunityId
          })
          this.userPreferences.preferredCommunityId = newPreferredCommunityId
          this.$bus.emit('updateUserProfile')
        } catch (error) {
          console.error('Error updating community preference:', error)
        }
      },
      async toggleSpottingAreaPreference (spottingAreaId) {
        try {
          const newPreferredSpottingAreaId = this.userPreferences.preferredSpottingAreaId === spottingAreaId ? null : spottingAreaId
          await updateUserPreferences({
            preferredSpottingAreaId: newPreferredSpottingAreaId
          })
          this.userPreferences.preferredSpottingAreaId = newPreferredSpottingAreaId
          this.$bus.emit('updateUserProfile')
        } catch (error) {
          console.error('Error updating community preference:', error)
        }
      }
    },
    components: {
      ActorAvatar,
      Avatar,
      ImageInput,
      UserSocialButtons,
      DsButton,
    },
    mounted () {
      this.user = this.currentUser()

      this.$bus.on('confirmAction', this.leaveCommunity)
      this.$bus.on('productAddedViaOverlay', (actor) => {
        const relation = [...actor.existingProductClaimsOfParent] || []
        relation.push({
          to: actor.id,
          to_name: actor.name,
          start: null,
          end: null,
        })
        updateActor({
          id: actor.parentId,
          data: {
            'has_product': relation,
          },
        }).then(() => {
          fetchProfile()
            .then(profile => {
              this.$store.commit('USER/UPDATE_PROFILE', profile)
              this.$bus.emit('updateUserProfile')
            })
        })
      })
      this.$bus.on('updateUserProfile', () => {
        this.user = this.currentUser()
        this.loadUserPreferences()
        this.fetchMyKnowledgeBasePosts()
      })
      this.$bus.on('fetchNewResources', () => {
        this.fetchMyKnowledgeBasePosts()
      })
      this.$bus.on('highlightCommunityCard', () => {
        this.highlightCommunityCard = true
      })
      this.$bus.on('highlightSpottingAreaCard', () => {
        this.highlightSpottingAreaCard = true
      })
      this.$bus.on('removeHighlight', () => {
        this.highlightCommunityCard = false
        this.highlightSpottingAreaCard = false
      })

      if (this.$store.getters.hasAccessToCommunities) {
        this.loadCommunities()
      }
      this.loadUserPreferences()
      this.fetchMyKnowledgeBasePosts()
    },
    beforeUnmount () {
      this.$bus.off('confirmAction')
      this.$bus.off('productAddedViaOverlay')
      this.$bus.off('updateUserProfile')
      this.$bus.off('fetchNewResources')
      this.$bus.off('highlightCommunityCard')
      this.$bus.off('highlightSpottingAreaCard')
      this.$bus.off('removeHighlight')
    },
    watch: {
      originalProfile: {
        handler (newProfile) {
          if (newProfile?.id) {
            this.fetchMyKnowledgeBasePosts()
          }
        },
        immediate: true
      }
    },
    mixins: [TranslationsMixin, OnboardingMixin, CommunityMixin, SpottingAreaMixin, UserMixin],
  }
</script>

<style lang="scss" scoped>
  /* Avatar hover effects */
  .relative:hover :deep(.avatar) .edit-icon {
    display: flex;
  }

  :deep(.avatar) img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  /* Remove webkit line clamp warning */
  .truncate-lines {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 4;
    line-clamp: 4;
    line-height: 14px;
    text-align: center;
    max-height: 56px;
    width: 100%;
  }
</style>
